import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type QueryClient,
} from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { Invoice, Trip, TripPaymentStatus } from "@shared/schema";
import type { InvoiceFormValues } from "@/components/invoicing/InvoiceFormCard";

/** Invalidate list + KPI + reports queries after invoice/trip mutations. */
export function invalidateInvoiceAndReportQueries(queryClient: QueryClient) {
  const prefixes = [
    "/api/invoices",
    "/api/invoices/mine",
    "/api/invoices/list",
    "/api/invoices/commission-kpis",
    "/api/invoices/net-commission-kpis",
    "/api/invoices/sales-kpis",
    "/api/invoices/trips-traveled-kpis",
    "/api/invoices/revenue-snapshot",
    "/api/invoices/avg-commission-per-trip",
    "/api/invoices/sales-commission-chart",
    "/api/invoices/chart-filter-options",
    "/api/invoices/supplier-insights",
    "/api/invoices/invoices-by-month",
    "/api/invoices/top-clients",
    "/api/invoices/advisor-activity",
    "/api/invoices/strategic-supplier-percentage",
    "/api/trips/mine",
    "/api/trips/recent-cards",
    "/api/trips/top-destinations",
  ] as const;
  for (const p of prefixes) {
    queryClient.invalidateQueries({ queryKey: [p] });
  }
}

/** Invalidate trip detail invoice lists after create/edit on that trip. */
export function invalidateTripInvoiceQueries(
  queryClient: QueryClient,
  tripId: number,
) {
  queryClient.invalidateQueries({ queryKey: [`/api/trips/${tripId}/invoices`] });
  queryClient.invalidateQueries({
    queryKey: [`/api/trips/${tripId}/invoices/summary`],
  });
}

function invalidateTripInvoiceQueriesForMove(
  queryClient: QueryClient,
  sourceTripId: number | undefined,
  destinationTripId: number | undefined,
) {
  const tripIds = new Set<number>();
  if (sourceTripId) tripIds.add(sourceTripId);
  if (destinationTripId) tripIds.add(destinationTripId);
  for (const tripId of tripIds) {
    invalidateTripInvoiceQueries(queryClient, tripId);
  }
}

export interface InvoiceListFilters {
  advisor?: string;
  tripId?: number;
  /** Alphanumeric trip record locator (the displayed "Trip ID") — substring match. */
  recordLocator?: string;
  invoiceNumber?: string;
  primaryTraveler?: string;
  supplier?: string;
  confirmationNumber?: string;
  departureDateFrom?: string;
  departureDateTo?: string;
  returnDateFrom?: string;
  returnDateTo?: string;
  invoiceStatusCode?: string;
  syncStatus?: string;
  createdFrom?: string;
  createdTo?: string;
  paymentDateFrom?: string;
  paymentDateTo?: string;
  totalFareMin?: string;
  totalFareMax?: string;
  commissionMin?: string;
  commissionMax?: string;
  /** Restrict to unclaimed-flow orphan invoices (no associated claim row). */
  missingClaim?: boolean;
}

export interface InvoiceListRow {
  id: number;
  teamId: number | null;
  userId: number | null;
  createdBy: number | null;
  tramsAccountId: number | null;
  tripId: number;
  invoiceNumber: string | null;
  /** TRAMS INVOICE.INVOICENUMBER (metadata.tramsInvoiceNumber). */
  tramsInvoiceNumber: string | null;
  recordLocator: string | null;
  agencyLinkNo: number | null;
  issueDate: string | null;
  invoiceStatusCode: string | null;
  primaryTraveler: string | null;
  supplierLinkNo: number | null;
  supplierName: string | null;
  travelType: string | null;
  confirmationNumber: string | null;
  departureDate: string | null;
  returnDate: string | null;
  totalFare: string | null;
  commissionAmount: string | null;
  /** This-row AGENTBKG.AMOUNT (net after the commission split). */
  agentCommissionAmount?: string | null;
  status: string;
  syncStatus: string;
  paidAt: string | null;
  statementDate: string | null;
  revision: number;
  createdAt: string;
  updatedAt: string;
  advisorName: string | null;
  paymentDate: string | null;
  /**
   * Set when an active unclaimed-commission claim references this
   * invoice. The Edit/Void affordances on this row should be disabled
   * with a tooltip pointing at `claimId`. Server enforces the same
   * lock — bypassing the UI returns 409 INVOICE_LOCKED_BY_UNCLAIMED_CLAIM.
   */
  lockedByClaim: { claimId: number; status: string } | null;
}

/**
 * Shape returned by `GET /api/invoices/:id`. Wraps the raw `Invoice`
 * with the same `lockedByClaim` annotation surfaced in the list, so the
 * detail/edit page can branch into a read-only banner without an extra
 * fetch.
 */
export type InvoiceWithLockState = Invoice & {
  lockedByClaim: { claimId: number; status: string } | null;
};

/**
 * AR-411: server-side sums over every row matching the current filters
 * (all pages). Numeric strings, 2dp; "0.00" when nothing matches.
 * `agentCommissionAmount` (Net Comm.) is only present for Agency Owners.
 */
export interface InvoiceListTotals {
  totalFare: string;
  commissionAmount: string;
  agentCommissionAmount?: string;
}

export interface InvoiceListResponse {
  items: InvoiceListRow[];
  totalCount: number;
  totals: InvoiceListTotals;
  viewMode: "my" | "all";
  hasCompanyAccess: boolean;
}

export interface InvoiceListParams {
  page: number;
  pageSize: number;
  sortBy: string;
  sortDir: "asc" | "desc";
  filters: InvoiceListFilters;
  tramsAccountId?: number;
  viewMode?: "my" | "all";
  refetchInterval?: number | false;
}

export interface CommissionKpis {
  /** Invoice log, by trip return date. Includes commission that has not paid yet. */
  invoicedYtd: string;
  /** Statement log, by the week that paid it. Not invoicedYtd minus outstandingYtd. */
  paidYtd: string;
  /** Invoice log, open invoices returning this year. */
  outstandingYtd: string;
  /** Statement log, gross less adjustments. Zero until the statement backfill lands. */
  netPaidYtd: string;
}

export function useCommissionKpis(params: {
  tramsAccountId?: number;
  viewMode?: "my" | "all";
  /** yyyy-mm-dd — invoice trip return date lower bound */
  returnDateFrom?: string;
  returnDateTo?: string;
}) {
  const qs = new URLSearchParams();
  if (params.tramsAccountId) qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);
  if (params.returnDateFrom) qs.set("returnDateFrom", params.returnDateFrom);
  if (params.returnDateTo) qs.set("returnDateTo", params.returnDateTo);

  return useQuery<CommissionKpis>({
    queryKey: ["/api/invoices/commission-kpis", params],
    queryFn: async () => {
      const res = await fetch(`/api/invoices/commission-kpis?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch commission KPIs");
      }
      return res.json();
    },
    staleTime: 30_000,
  });
}

export interface NetCommissionKpis {
  netCommissionYtd: string;
}

/**
 * YTD Net commission (TRAMS Agent Commission field). Agency Owner only —
 * the API returns 403 for other roles, so gate with `enabled`.
 */
export function useNetCommissionKpis(params: {
  tramsAccountId?: number;
  viewMode?: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
  enabled?: boolean;
}) {
  const qs = new URLSearchParams();
  if (params.tramsAccountId) qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);
  if (params.returnDateFrom) qs.set("returnDateFrom", params.returnDateFrom);
  if (params.returnDateTo) qs.set("returnDateTo", params.returnDateTo);

  return useQuery<NetCommissionKpis>({
    queryKey: ["/api/invoices/net-commission-kpis", params],
    queryFn: async () => {
      const res = await fetch(`/api/invoices/net-commission-kpis?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch net commission KPIs");
      }
      return res.json();
    },
    staleTime: 30_000,
    enabled: params.enabled ?? true,
  });
}

export interface StrategicSupplierData {
  percentage: number;
  hasActivity: boolean;
}

export function useStrategicSupplierPercentage(params?: { tramsAccountId?: number; viewMode?: "my" | "all" }) {
  const qs = new URLSearchParams();
  if (params?.tramsAccountId) qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params?.viewMode) qs.set("viewMode", params.viewMode);

  return useQuery<StrategicSupplierData>({
    queryKey: ["/api/invoices/strategic-supplier-percentage", params],
    queryFn: async () => {
      const url = `/api/invoices/strategic-supplier-percentage${qs.size ? `?${qs.toString()}` : ""}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch strategic supplier percentage");
      }
      return res.json();
    },
    staleTime: 60_000,
  });
}

export interface SalesKpis {
  invoicedYtd: string;
  paidYtd: string;
  outstandingYtd: string;
}

export function useSalesKpis(params: {
  tramsAccountId?: number;
  viewMode?: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
}) {
  const qs = new URLSearchParams();
  if (params.tramsAccountId) qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);
  if (params.returnDateFrom) qs.set("returnDateFrom", params.returnDateFrom);
  if (params.returnDateTo) qs.set("returnDateTo", params.returnDateTo);

  return useQuery<SalesKpis>({
    queryKey: ["/api/invoices/sales-kpis", params],
    queryFn: async () => {
      const res = await fetch(`/api/invoices/sales-kpis?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch sales KPIs");
      }
      return res.json();
    },
    staleTime: 30_000,
  });
}

export interface TripsTraveledKpis {
  tripsTraveledYtd: number;
  tripsInvoicedYtd: number;
}

export function useTripsTraveledKpis(params: {
  tramsAccountId?: number;
  viewMode?: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
}) {
  const qs = new URLSearchParams();
  if (params.tramsAccountId) qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);
  if (params.returnDateFrom) qs.set("returnDateFrom", params.returnDateFrom);
  if (params.returnDateTo) qs.set("returnDateTo", params.returnDateTo);

  return useQuery<TripsTraveledKpis>({
    queryKey: ["/api/invoices/trips-traveled-kpis", params],
    queryFn: async () => {
      const res = await fetch(`/api/invoices/trips-traveled-kpis?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch trips traveled KPIs");
      }
      return res.json();
    },
    staleTime: 30_000,
  });
}

export interface RevenueSnapshotData {
  avgSalePerTrip: string;
  avgCommissionPerTrip: string;
  tripCount12m: number;
  projectedNextYearSales: string;
  projectedNextYearCommission: string;
  projectedNextYearTripCount: number;
}

export function useRevenueSnapshot(params: {
  tramsAccountId?: number;
  viewMode?: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
}) {
  const qs = new URLSearchParams();
  if (params.tramsAccountId) qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);
  if (params.returnDateFrom) qs.set("returnDateFrom", params.returnDateFrom);
  if (params.returnDateTo) qs.set("returnDateTo", params.returnDateTo);

  return useQuery<RevenueSnapshotData>({
    queryKey: ["/api/invoices/revenue-snapshot", params],
    queryFn: async () => {
      const res = await fetch(`/api/invoices/revenue-snapshot?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch revenue snapshot");
      }
      return res.json();
    },
    staleTime: 30_000,
  });
}

export interface AvgCommissionPerTripKpis {
  totalCommission: string;
  tripCount: number;
  avgCommission: string;
}

export function useAvgCommissionPerTrip(params: {
  tramsAccountId?: number;
  viewMode?: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
}) {
  const qs = new URLSearchParams();
  if (params.tramsAccountId) qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);
  if (params.returnDateFrom) qs.set("returnDateFrom", params.returnDateFrom);
  if (params.returnDateTo) qs.set("returnDateTo", params.returnDateTo);

  return useQuery<AvgCommissionPerTripKpis>({
    queryKey: ["/api/invoices/avg-commission-per-trip", params],
    queryFn: async () => {
      const res = await fetch(`/api/invoices/avg-commission-per-trip?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch avg commission per trip");
      }
      return res.json();
    },
    staleTime: 30_000,
  });
}

/** Query params shared by the list and CSV-export endpoints (pagination optional). */
export type InvoiceListQuery = Omit<InvoiceListParams, "refetchInterval" | "page" | "pageSize"> &
  Partial<Pick<InvoiceListParams, "page" | "pageSize">>;

/**
 * Serializes list/export query params. Used by both useInvoiceList and the
 * CSV export so the "currently applied filters" can never diverge.
 */
export function buildInvoiceListQuery(params: InvoiceListQuery): URLSearchParams {
  const qs = new URLSearchParams();
  if (params.page !== undefined) qs.set("page", String(params.page));
  if (params.pageSize !== undefined) qs.set("pageSize", String(params.pageSize));
  qs.set("sortBy", params.sortBy);
  qs.set("sortDir", params.sortDir);
  if (params.tramsAccountId) qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);

  const f = params.filters;
  if (f.advisor) qs.set("advisor", f.advisor);
  if (f.tripId) qs.set("tripId", String(f.tripId));
  if (f.recordLocator) qs.set("recordLocator", f.recordLocator);
  if (f.invoiceNumber) qs.set("invoiceNumber", String(f.invoiceNumber));
  if (f.primaryTraveler) qs.set("primaryTraveler", f.primaryTraveler);
  if (f.supplier) qs.set("supplier", f.supplier);
  if (f.confirmationNumber) qs.set("confirmationNumber", f.confirmationNumber);
  if (f.departureDateFrom) qs.set("departureDateFrom", f.departureDateFrom);
  if (f.departureDateTo) qs.set("departureDateTo", f.departureDateTo);
  if (f.returnDateFrom) qs.set("returnDateFrom", f.returnDateFrom);
  if (f.returnDateTo) qs.set("returnDateTo", f.returnDateTo);
  if (f.invoiceStatusCode) qs.set("invoiceStatusCode", f.invoiceStatusCode);
  if (f.syncStatus) qs.set("syncStatus", f.syncStatus);
  if (f.createdFrom) qs.set("createdFrom", f.createdFrom);
  if (f.createdTo) qs.set("createdTo", f.createdTo);
  if (f.paymentDateFrom) qs.set("paymentDateFrom", f.paymentDateFrom);
  if (f.paymentDateTo) qs.set("paymentDateTo", f.paymentDateTo);
  if (f.totalFareMin) qs.set("totalFareMin", f.totalFareMin);
  if (f.totalFareMax) qs.set("totalFareMax", f.totalFareMax);
  if (f.commissionMin) qs.set("commissionMin", f.commissionMin);
  if (f.commissionMax) qs.set("commissionMax", f.commissionMax);
  if (f.missingClaim) qs.set("missingClaim", "true");
  return qs;
}

export function useInvoiceList(params: InvoiceListParams) {
  const { refetchInterval, ...queryParams } = params;
  const qs = buildInvoiceListQuery(queryParams);

  return useQuery<InvoiceListResponse>({
    queryKey: ["/api/invoices/list", queryParams],
    queryFn: async () => {
      const res = await fetch(`/api/invoices/list?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch invoices");
      }
      return res.json();
    },
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    refetchInterval,
  });
}

export interface CreateInvoicePayload {
  tripId: number;
  travelerFirstName: string;
  travelerLastName: string;
  /** Composed as "LastName/FirstName" for the atomic endpoint. */
  primaryTraveler?: string;
  supplierLinkNo?: number;
  supplierName?: string;
  travelType: string;
  propertyName?: string;
  shipName?: string;
  cabinCount?: number;
  tourName?: string;
  description?: string;
  confirmationNumber: string;
  departureDate: string;
  returnDate: string;
  totalFare: string;
  commissionAmount: string;
  agencyLinkNo: number;
  tramsAccountId?: number;
}

export interface DestinationPayload {
  id: string;
  displayName: string;
  destinationType: string;
  parentRegion?: string | null;
}

export interface CreateTripPayload {
  name: string;
  destinations?: DestinationPayload[];
  isGroup?: boolean;
  groupName?: string;
}

export interface AtomicInvoiceSubmitPayload {
  trip:
    | { mode: "existing"; tripId: number }
    | { mode: "new"; name: string; destinations: DestinationPayload[]; isGroup: boolean; groupName?: string };
  invoices: Omit<CreateInvoicePayload, "tripId">[];
  idempotencyKey: string;
  /** 'unclaimed' = pre-creation from the unclaimed-claim wizard — suppresses the confirmation email. */
  context?: "unclaimed";
}

export function useInvoicesByTrip(
  tripId: number | undefined,
  tramsAccountId?: number,
  highlightInvoiceId?: number,
) {
  const qs = new URLSearchParams();
  if (tramsAccountId != null) qs.set("tramsAccountId", String(tramsAccountId));
  if (highlightInvoiceId != null) qs.set("highlightInvoiceId", String(highlightInvoiceId));
  const query = qs.toString();
  return useQuery<Invoice[]>({
    queryKey: [`/api/trips/${tripId}/invoices`, { tramsAccountId, highlightInvoiceId }],
    queryFn: () =>
      apiRequest("GET", `/api/trips/${tripId}/invoices${query ? `?${query}` : ""}`),
    enabled: !!tripId,
    staleTime: 30_000,
  });
}

export function useUpdateTrip() {
  const queryClient = useQueryClient();

  return useMutation<Trip, Error, { id: number; data: Record<string, unknown> }>({
    mutationFn: ({ id, data }) => apiRequest("PATCH", `/api/trips/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [`/api/trips/${variables.id}`] });
      queryClient.invalidateQueries({ queryKey: ["/api/trips/mine"] });
      queryClient.invalidateQueries({ queryKey: ["/api/trips/recent-cards"] });
      invalidateInvoiceAndReportQueries(queryClient);
    },
  });
}

export function useCommissionInquiry() {
  const queryClient = useQueryClient();

  return useMutation<Invoice, Error, { id: number; notes?: string }>({
    mutationFn: async ({ id, notes }) => {
      const res = await fetch(`/api/invoices/${id}/commission-inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ notes }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to submit commission inquiry");
      }
      return res.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices", variables.id] });
      if (data?.tripId) {
        queryClient.invalidateQueries({ queryKey: [`/api/trips/${data.tripId}/invoices`] });
        queryClient.invalidateQueries({ queryKey: [`/api/trips/${data.tripId}/invoices/summary`] });
      }
      invalidateInvoiceAndReportQueries(queryClient);
    },
  });
}

export function useInvoicesByUser(tramsAccountId?: number) {
  const params = new URLSearchParams({ userId: "me" });
  if (tramsAccountId) {
    params.set("tramsAccountId", String(tramsAccountId));
  }
  return useQuery<Invoice[]>({
    queryKey: ["/api/invoices/mine", { tramsAccountId }],
    queryFn: async () => {
      const res = await fetch(`/api/invoices?${params.toString()}`, { credentials: "include" });
      if (!res.ok) {
        throw new Error(`Failed to fetch invoices: ${res.status} ${res.statusText}`);
      }
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useTrips() {
  return useQuery<Trip[]>({
    queryKey: ["/api/trips/mine"],
    queryFn: async () => {
      const res = await fetch("/api/trips/mine", { credentials: "include" });
      if (!res.ok) {
        throw new Error(`Failed to fetch trips: ${res.status} ${res.statusText}`);
      }
      return res.json();
    },
    staleTime: 30_000,
  });
}

export function useTripSearch(searchTerm: string) {
  const { data: allTrips, ...rest } = useTrips();
  const filtered =
    searchTerm.trim().length > 0 && allTrips
      ? allTrips.filter(
          (t) =>
            t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (t.recordLocator ?? "").toLowerCase().includes(searchTerm.toLowerCase())
        )
      : allTrips ?? [];

  return { data: filtered, ...rest };
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation<Invoice, Error, CreateInvoicePayload & { idempotencyKey?: string }>({
    mutationFn: async (payload) => {
      const { idempotencyKey, ...data } = payload;
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
      const res = await fetch("/api/invoices", {
        method: "POST",
        headers,
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to create invoice");
      }
      return res.json();
    },
    onSuccess: (data) => {
      if (data?.tripId) {
        invalidateTripInvoiceQueries(queryClient, data.tripId);
      }
      invalidateInvoiceAndReportQueries(queryClient);
    },
  });
}

export function useCreateTrip() {
  const queryClient = useQueryClient();

  return useMutation<Trip, Error, CreateTripPayload>({
    mutationFn: (data) => apiRequest("POST", "/api/trips", data),
    onSuccess: () => {
      invalidateInvoiceAndReportQueries(queryClient);
    },
  });
}

export function useInvoice(id: number | undefined) {
  return useQuery<InvoiceWithLockState>({
    queryKey: ["/api/invoices", id],
    queryFn: () => apiRequest("GET", `/api/invoices/${id}`),
    enabled: !!id,
    staleTime: 15_000,
  });
}

/**
 * Error subclass thrown by the update/void mutations when the server
 * rejects the operation because an active unclaimed-commission claim
 * references the invoice. Mutation consumers can `instanceof`-check this
 * and branch toast copy / route the user back to the invoice list rather
 * than showing the generic failure banner.
 */
export class InvoiceLockedByClaimError extends Error {
  readonly code = "INVOICE_LOCKED_BY_UNCLAIMED_CLAIM" as const;
  constructor(
    message: string,
    public readonly claimId: number | null,
    public readonly claimStatus: string | null,
  ) {
    super(message);
    this.name = "InvoiceLockedByClaimError";
  }
}

/**
 * Reads the typed error envelope produced by NestJS exception filters.
 * Some classes (HttpException) nest the body under `data.message`;
 * others put it at the root. We probe both.
 */
function parseLockedByClaimError(body: any): InvoiceLockedByClaimError | null {
  if (!body || typeof body !== "object") return null;
  const node =
    body.code === "INVOICE_LOCKED_BY_UNCLAIMED_CLAIM"
      ? body
      : body.message && typeof body.message === "object" && body.message.code === "INVOICE_LOCKED_BY_UNCLAIMED_CLAIM"
        ? body.message
        : null;
  if (!node) return null;
  return new InvoiceLockedByClaimError(
    typeof node.message === "string"
      ? node.message
      : "This invoice is locked by an unclaimed-commission claim under review.",
    typeof node.claimId === "number" ? node.claimId : null,
    typeof node.claimStatus === "string" ? node.claimStatus : null,
  );
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient();

  return useMutation<Invoice, Error, { id: number; data: Partial<InvoiceFormValues>; operationId?: string }>({
    mutationFn: async ({ id, data, operationId }) => {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (operationId) headers["X-Operation-Id"] = operationId;
      const res = await fetch(`/api/invoices/${id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const locked = parseLockedByClaimError(body);
        if (locked) throw locked;
        throw new Error(body.message || "Failed to update invoice");
      }
      return res.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices", variables.id] });
      if (data?.tripId) {
        invalidateTripInvoiceQueries(queryClient, data.tripId);
      }
      invalidateInvoiceAndReportQueries(queryClient);
    },
  });
}

export interface MoveInvoiceResponse {
  invoice: Invoice;
  auditEventId: number;
}

export function useMoveInvoice() {
  const queryClient = useQueryClient();

  return useMutation<
    MoveInvoiceResponse,
    Error,
    { id: number; targetTripId: number; sourceTripId?: number; reason?: string; operationId?: string }
  >({
    mutationFn: async ({ id, targetTripId, reason, operationId }) => {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (operationId) headers["X-Operation-Id"] = operationId;
      const res = await fetch(`/api/invoices/${id}/move`, {
        method: "POST",
        headers,
        body: JSON.stringify({ targetTripId, reason }),
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to move invoice");
      }
      return res.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices", variables.id] });
      invalidateTripInvoiceQueriesForMove(
        queryClient,
        variables.sourceTripId,
        data.invoice.tripId ?? undefined,
      );
      invalidateInvoiceAndReportQueries(queryClient);
    },
  });
}

export interface MoveInvoiceToNewTripPayload {
  id: number;
  sourceTripId?: number;
  trip: {
    name: string;
    destinations: DestinationPayload[];
    isGroup: boolean;
    groupName?: string;
  };
  reason?: string;
  operationId?: string;
}

export interface MoveInvoiceToNewTripResponse {
  invoice: Invoice;
  trip: Trip;
  auditEventId: number;
}

/**
 * Atomic alternative to `useMoveInvoice`: creates a new trip server-side and
 * moves the invoice into it in a single request. If the move fails the
 * server cleans up the just-created trip, so the client never has to babysit
 * an orphan trip on failure.
 */
export function useMoveInvoiceToNewTrip() {
  const queryClient = useQueryClient();

  return useMutation<
    MoveInvoiceToNewTripResponse,
    Error,
    MoveInvoiceToNewTripPayload
  >({
    mutationFn: async ({ id, trip, reason, operationId }) => {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (operationId) headers["X-Operation-Id"] = operationId;
      const res = await fetch(`/api/invoices/${id}/move-to-new-trip`, {
        method: "POST",
        headers,
        body: JSON.stringify({ trip, reason }),
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to move invoice to new trip");
      }
      return res.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices", variables.id] });
      invalidateTripInvoiceQueriesForMove(
        queryClient,
        variables.sourceTripId,
        data.invoice.tripId ?? undefined,
      );
      queryClient.invalidateQueries({ queryKey: ["/api/trips/mine"] });
      invalidateInvoiceAndReportQueries(queryClient);
    },
  });
}

export function useVoidInvoice() {
  const queryClient = useQueryClient();

  return useMutation<Invoice, Error, { id: number; reason?: string; operationId?: string }>({
    mutationFn: async ({ id, reason, operationId }) => {
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (operationId) headers["X-Operation-Id"] = operationId;
      const res = await fetch(`/api/invoices/${id}/void`, {
        method: "POST",
        headers,
        body: JSON.stringify({ reason }),
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const locked = parseLockedByClaimError(body);
        if (locked) throw locked;
        throw new Error(body.message || "Failed to void invoice");
      }
      return res.json();
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/invoices", variables.id] });
      if (data?.tripId) {
        queryClient.invalidateQueries({ queryKey: [`/api/trips/${data.tripId}/invoices`] });
        queryClient.invalidateQueries({ queryKey: [`/api/trips/${data.tripId}/invoices/summary`] });
      }
      invalidateInvoiceAndReportQueries(queryClient);
    },
  });
}

export function useAtomicInvoiceSubmit() {
  const queryClient = useQueryClient();

  return useMutation<{ trip: Trip; invoices: Invoice[] }, Error, AtomicInvoiceSubmitPayload>({
    mutationFn: (data) => apiRequest("POST", "/api/trips/with-invoices", data),
    onSuccess: () => {
      invalidateInvoiceAndReportQueries(queryClient);
    },
  });
}

// ---------------------------------------------------------------------------
// Sales + Commission Chart
// ---------------------------------------------------------------------------

export interface ChartDataPoint {
  month: string;
  paidCommission: string;
  openCommission: string;
  paidSales: string;
  openSales: string;
  /** Net advisor commission (TRAMS agentAmount) — present for Agency Owners only. */
  paidAdvisorCommission?: string;
  openAdvisorCommission?: string;
}

export interface SalesCommissionChartResponse {
  data: ChartDataPoint[];
  viewMode: "my" | "all";
  /**
   * Populated by the server when the effective basis has diverged from the
   * shared `salesCommissionChart` KPI_BASIS default (e.g. the return-date
   * filter branch answers both series from the invoice log). Absent when the
   * standard mixed basis applies, so the card falls back to the shared label.
   */
  basisOverride?: {
    label: string;
    note?: string;
  };
}

export interface SalesCommissionChartParams {
  tramsAccountId?: number;
  viewMode?: "my" | "all";
  dateFrom?: string;
  dateTo?: string;
  /** Global Reports trip return date filter (invoice return_date), in addition to chart date range */
  returnDateFrom?: string;
  returnDateTo?: string;
  suppliers?: string[];
  travelTypes?: string[];
  advisorIds?: number[];
}

export function useSalesCommissionChart(params: SalesCommissionChartParams) {
  const qs = new URLSearchParams();
  if (params.tramsAccountId) qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);
  if (params.dateFrom) qs.set("dateFrom", params.dateFrom);
  if (params.dateTo) qs.set("dateTo", params.dateTo);
  if (params.returnDateFrom) qs.set("returnDateFrom", params.returnDateFrom);
  if (params.returnDateTo) qs.set("returnDateTo", params.returnDateTo);
  for (const s of params.suppliers ?? []) {
    qs.append("suppliers", s);
  }
  for (const t of params.travelTypes ?? []) {
    qs.append("travelTypes", t);
  }
  for (const id of params.advisorIds ?? []) {
    qs.append("advisorIds", String(id));
  }

  return useQuery<SalesCommissionChartResponse>({
    queryKey: ["/api/invoices/sales-commission-chart", params],
    queryFn: async () => {
      const res = await fetch(`/api/invoices/sales-commission-chart?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch chart data");
      }
      return res.json();
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export interface ChartFilterOptions {
  suppliers: string[];
  travelTypes: string[];
  advisors: Array<{ id: number; name: string }>;
}

export function useChartFilterOptions(params: {
  tramsAccountId?: number;
  viewMode?: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
}) {
  const qs = new URLSearchParams();
  if (params.tramsAccountId) qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);
  if (params.returnDateFrom) qs.set("returnDateFrom", params.returnDateFrom);
  if (params.returnDateTo) qs.set("returnDateTo", params.returnDateTo);

  return useQuery<ChartFilterOptions>({
    queryKey: ["/api/invoices/chart-filter-options", params],
    queryFn: async () => {
      const res = await fetch(`/api/invoices/chart-filter-options?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch chart filter options");
      }
      return res.json();
    },
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Supplier Insights
// ---------------------------------------------------------------------------

export interface SupplierInsightRow {
  supplier: string;
  invoiceCount: number;
  totalSales: string;
  totalCommission: string;
}

export interface SupplierInsightsResponse {
  data: SupplierInsightRow[];
  viewMode: "my" | "all";
}

export interface SupplierInsightsParams {
  tramsAccountId?: number;
  viewMode?: "my" | "all";
  advisorIds?: number[];
  returnDateFrom?: string;
  returnDateTo?: string;
}

export function useSupplierInsights(params: SupplierInsightsParams) {
  const qs = new URLSearchParams();
  if (params.tramsAccountId) qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);
  if (params.returnDateFrom) qs.set("returnDateFrom", params.returnDateFrom);
  if (params.returnDateTo) qs.set("returnDateTo", params.returnDateTo);
  for (const id of params.advisorIds ?? []) {
    qs.append("advisorIds", String(id));
  }

  return useQuery<SupplierInsightsResponse>({
    queryKey: ["/api/invoices/supplier-insights", params],
    queryFn: async () => {
      const res = await fetch(`/api/invoices/supplier-insights?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch supplier insights");
      }
      return res.json();
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export interface InvoicesByMonthBucket {
  month: string;
  closedCount: number;
  openCount: number;
}

export interface InvoicesByMonthResponse {
  data: InvoicesByMonthBucket[];
  viewMode: "my" | "all";
}

export function useInvoicesByMonth(params: {
  tramsAccountId?: number;
  viewMode?: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
}) {
  const qs = new URLSearchParams();
  if (params.tramsAccountId) qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);
  if (params.returnDateFrom) qs.set("returnDateFrom", params.returnDateFrom);
  if (params.returnDateTo) qs.set("returnDateTo", params.returnDateTo);

  return useQuery<InvoicesByMonthResponse>({
    queryKey: ["/api/invoices/invoices-by-month", params],
    queryFn: async () => {
      const res = await fetch(`/api/invoices/invoices-by-month?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch invoices by month");
      }
      return res.json();
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export type { TripPaymentStatus };

export interface TripCardData {
  tripId: number;
  tripName: string;
  recordLocator: string | null;
  tripUpdatedAt: string;
  invoiceCount: number;
  totalSales: string;
  totalCommission: string;
  paymentStatus: TripPaymentStatus;
}

export interface TripWithInvoiceSummary {
  tripId: number;
  tripName: string;
  recordLocator: string | null;
  totalSales: number;
  totalCommission: number;
  invoiceCount: number;
  paymentStatus: TripPaymentStatus;
}

export function useRecentTripsWithSummaries(limit?: number) {
  const { data, isLoading, error, refetch } = useQuery<TripCardData[]>({
    queryKey: ["/api/trips/recent-cards", limit ?? "all"],
    queryFn: async () => {
      const url =
        limit != null
          ? `/api/trips/recent-cards?limit=${limit}`
          : "/api/trips/recent-cards";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch recent trips");
      }
      return res.json();
    },
    staleTime: 30_000,
  });

  const results: TripWithInvoiceSummary[] = (data ?? []).map((card) => ({
    tripId: card.tripId,
    tripName: card.tripName,
    recordLocator: card.recordLocator,
    totalSales: parseFloat(card.totalSales || "0"),
    totalCommission: parseFloat(card.totalCommission || "0"),
    invoiceCount: card.invoiceCount,
    paymentStatus: card.paymentStatus,
  }));

  return { data: results, isLoading, error, refetch };
}

// ---------------------------------------------------------------------------
// Top Destinations
// ---------------------------------------------------------------------------

export interface TopDestinationRow {
  destination: string;
  destinationType: string;
  region: string | null;
  amount: number;
  totalSales: string;
  totalCommission: string;
}

export interface TopDestinationsResponse {
  data: TopDestinationRow[];
  viewMode: "my" | "all";
}

export interface TopDestinationsParams {
  tramsAccountId?: number;
  viewMode?: "my" | "all";
  advisorIds?: number[];
  /** ISO date string yyyy-mm-dd */
  returnDateFrom?: string;
  returnDateTo?: string;
}

export function useTopDestinations(params: TopDestinationsParams) {
  const qs = new URLSearchParams();
  if (params.tramsAccountId) qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);
  if (params.returnDateFrom) qs.set("returnDateFrom", params.returnDateFrom);
  if (params.returnDateTo) qs.set("returnDateTo", params.returnDateTo);
  for (const id of params.advisorIds ?? []) {
    qs.append("advisorIds", String(id));
  }

  return useQuery<TopDestinationsResponse>({
    queryKey: ["/api/trips/top-destinations", params],
    queryFn: async () => {
      const res = await fetch(`/api/trips/top-destinations?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch top destinations");
      }
      return res.json();
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Top Clients
// ---------------------------------------------------------------------------

export interface TopClientRow {
  clientName: string;
  tripCount: number;
  totalSales: string;
  totalCommission: string;
}

export interface TopClientsResponse {
  data: TopClientRow[];
  viewMode: "my" | "all";
}

export interface TopClientsParams {
  tramsAccountId?: number;
  viewMode?: "my" | "all";
  advisorIds?: number[];
  returnDateFrom?: string;
  returnDateTo?: string;
}

export function useTopClients(params: TopClientsParams) {
  const qs = new URLSearchParams();
  if (params.tramsAccountId) qs.set("tramsAccountId", String(params.tramsAccountId));
  if (params.viewMode) qs.set("viewMode", params.viewMode);
  if (params.returnDateFrom) qs.set("returnDateFrom", params.returnDateFrom);
  if (params.returnDateTo) qs.set("returnDateTo", params.returnDateTo);
  for (const id of params.advisorIds ?? []) {
    qs.append("advisorIds", String(id));
  }

  return useQuery<TopClientsResponse>({
    queryKey: ["/api/invoices/top-clients", params],
    queryFn: async () => {
      const res = await fetch(`/api/invoices/top-clients?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch top clients");
      }
      return res.json();
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Advisor Activity Log
// ---------------------------------------------------------------------------

export interface AdvisorActivityRow {
  advisorId: number;
  advisorName: string;
  tripCount: number;
  invoiceCount: number;
  totalSales: string;
  totalCommission: string;
  lastInvoiceDate: string | null;
  gemStatus: string | null;
}

export interface AdvisorActivityResponse {
  data: AdvisorActivityRow[];
  viewMode: "my" | "all";
  /**
   * The row reads two logs, so its basis is configured on the server and travels with
   * the data rather than being restated by the card.
   */
  basis?: {
    dateBasis: "trip_return_date" | "statement_week";
    reportingExcluded: "exclude" | "include_invoices" | "include_all";
  };
  basisLabel?: string;
}

export interface AdvisorActivityParams {
  viewMode?: "my" | "all";
  enabled?: boolean;
  returnDateFrom?: string;
  returnDateTo?: string;
}

export function useAdvisorActivity(params: AdvisorActivityParams) {
  const qs = new URLSearchParams();
  if (params.viewMode) qs.set("viewMode", params.viewMode);
  if (params.returnDateFrom) qs.set("returnDateFrom", params.returnDateFrom);
  if (params.returnDateTo) qs.set("returnDateTo", params.returnDateTo);

  return useQuery<AdvisorActivityResponse>({
    queryKey: ["/api/invoices/advisor-activity", params],
    queryFn: async () => {
      const res = await fetch(`/api/invoices/advisor-activity?${qs.toString()}`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to fetch advisor activity");
      }
      return res.json();
    },
    enabled: params.enabled !== false,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export interface ImportTramsHistoryResult {
  imported: number;
  /** Rows the agency owner's import held that were moved to this advisor. */
  refiled: number;
  skipped: number;
  errors: string[];
}

export function useImportTramsHistory() {
  const queryClient = useQueryClient();

  return useMutation<ImportTramsHistoryResult, Error, { tramsAccountId?: number }>({
    mutationFn: async ({ tramsAccountId }) => {
      const res = await fetch("/api/invoices/import-history", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(tramsAccountId != null ? { tramsAccountId } : {}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.message || "Failed to import TRAMS history");
      }
      return body as ImportTramsHistoryResult;
    },
    onSuccess: () => {
      invalidateInvoiceAndReportQueries(queryClient);
    },
  });
}
